'use client';

import {v4 as uuidv4} from "uuid";
import PropTypes from 'prop-types';
import {useSnackbar} from "notistack";
import {useRouter} from "next/navigation";
import React, {useState, useCallback} from "react";
import {getThumbnailUrl} from "@/utils/image-utils.ts";

import Card from '@mui/material/Card';
import Link from "@mui/material/Link";
import Stack from '@mui/material/Stack';
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import {styled} from '@mui/material/styles';
import Typography from "@mui/material/Typography";

import {paths} from "src/routes/paths";
import {RouterLink} from "src/routes/components";

import useCommentListForRecipe from "src/hooks/comment/use-comment-list-for-recipe";

import {fDateTime} from 'src/utils/format-time';

import {useAuthContext} from "src/auth/hooks";

import CommentForm from "./comment-form";
import CommentVote from "./comment-vote";
import {LoadingScreen} from "../loading-screen";

// ----------------------------------------------------------------------
const StyledLink = styled(Link)(({theme}) => ({
  '&:hover': {
    color: theme.palette.grey[900],
    cursor: 'pointer'
  }
}));

const StyledCard = styled(Card)(({theme}) => ({
  borderRadius: 2,
  display: 'flex',
  flexGrow: 1
}));

const ReplyArrow = styled('span')(({theme}) => ({
  width: '20px',
  height: '12px',
  marginBottom: '3px',
  display: 'inline-block',
  borderBottom: '1px solid',
  borderLeft: '1px solid',
  borderColor: theme.palette.text.secondary,
  borderRadius: '0 0 0 3px',
  marginRight: '5px'
}));

export default function CommentItem({recipeData, comment, ...others}) {
  const {enqueueSnackbar} = useSnackbar();
  const router = useRouter();
  const {user} = useAuthContext();

  const [commentListSize, setCommentListSize] = useState(10);
  const [showReply, setShowReply] = useState(false);
  const [showReplies, setShowReplies] = useState(false);

  const {
    commentList,
    isLoaded: isCommentListLoaded,
    loadCommentData,
    setIsLoaded: setIsCommentListLoaded
  } = useCommentListForRecipe(comment.recipeId, comment.pimcoreCommentId);

  const handleReply = useCallback(
    () => {
      if (!user || user.isLoggedIn === false) {
        enqueueSnackbar('Musisz być zalogowany, aby odpowiadać na komentarze.', {variant: 'error'});
        router.push(paths.auth.token.loginWithReturnTo(paths.recipe.details(recipeData.pimcoreRecipeId)));
        return;
      }

      setShowReply(true);
    },
    [enqueueSnackbar, router, user, recipeData.pimcoreRecipeId, setShowReply]
  );

  const handleShowReply = useCallback(
    () => {
      loadCommentData(commentListSize);
      setShowReplies(true);
    },
    [commentListSize, loadCommentData, setShowReplies]
  );

  const handleAfterCommentSubmit = useCallback(
    () => {
      loadCommentData(commentListSize);
      setShowReply(false);
      setShowReplies(true);
    },
    [commentListSize, loadCommentData, setShowReply, setShowReplies]
  );

  const handleShowMore = useCallback(() => {
    setCommentListSize(prev => prev + 10);
    setIsCommentListLoaded(false);
    loadCommentData(commentListSize + 10);
  }, [commentListSize, setIsCommentListLoaded, setCommentListSize, loadCommentData]);


  const showShowShowRepliesText = (!showReplies && comment.replyCount > 0);

  const showReplyText = `Zobacz ${comment.replyCount} ${comment.replyCount > 1 ? 'odpowiedzi' : 'odpowiedź'}`;

  const renderReplyBox = () => (
    <Stack direction="column" spacing={2} sx={{ml: 8}}>
      {showReply && (
        <>
          <Typography variant="subtitle1">
            {`Odpowiadasz na komentarz ${comment.userModel?.name}`}
          </Typography>
          <CommentForm
            recipeId={parseInt(comment.recipeId, 10)}
            replyToCommentId={comment.pimcoreCommentId}
            afterSubmit={handleAfterCommentSubmit}
          />
        </>
      )}
      {showReplies && (
        <>
          {commentList.items?.map(replyComment => (
            <CommentItem key={`${uuidv4()}-${replyComment.id}`} recipeData={recipeData} comment={replyComment}/>
          ))}
          {!isCommentListLoaded && (
            <LoadingScreen sx={{mt: 1}}/>
          )}
        </>
      )}
      {showShowShowRepliesText && (
        <StyledLink onClick={handleShowReply} color="text.secondary" title={showReplyText}>
          <ReplyArrow/>
          <Typography variant="caption">{showReplyText}</Typography>
        </StyledLink>
      )}
      {(isCommentListLoaded && commentList.totalCount > commentList.items?.length) && (
        <Stack direction="row" sx={{justifyContent: 'center'}}>
          <Button sx={{width: {xs: '100%', sm: 'auto'}}} color="primary" variant="outlined" onClick={handleShowMore}>
            Załaduj więcej odpowiedzi
          </Button>
        </Stack>
      )}
    </Stack>
  );

  const renderVoteBox = () => (
    <CommentVote comment={comment} recipeData={recipeData}/>
  );

  return (
    <Stack direction="column" spacing={2}>
      <Stack direction="row" spacing={2} sx={{alignItems: 'center'}}>
        <StyledCard {...others}>
          <Stack
            spacing={2}
            direction="row"
            alignItems="top"
          >
            <Link
              component={RouterLink}
              href={paths.user.profile(comment.userId)}
              sx={{
                ml: 1,
                my: 1,
                display: 'block',
                '&:hover': {
                  opacity: 0.8
                }
              }}
            >
              <Avatar
                src={getThumbnailUrl(comment.userModel.photo, '270_270')}
                alt={comment.userModel?.photo?.filename}
                sx={{
                  width: 42,
                  height: 42,
                  border: (theme) => `solid 2px ${theme.palette.background.default}`,
                }}
              >
                {comment.userModel?.name?.charAt(0).toUpperCase()}
              </Avatar>
            </Link>
            <Stack
              spacing={1}
              direction="column"
              sx={{
                my: 1
              }}
            >
              <Stack
                spacing={1}
                direction="row">
                <Link
                  component={RouterLink}
                  href={paths.user.profile(comment.userId)}
                  variant="caption"
                  sx={{
                    color: 'primary.main',
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  {comment.userModel?.name}
                </Link>
                <Typography variant="caption"
                            color="text.secondary">{`${fDateTime(comment.createdAt * 1000)}`}</Typography>
              </Stack>
              {!comment.published && (
                <Typography variant="caption">Weryfikujemy Twój komentarz. Tymczasem widzisz go tylko Ty.</Typography>
              )}
              <Typography variant="body2">{comment.content}</Typography>
              <Stack
                spacing={1}
                direction="row">
                <StyledLink onClick={handleReply} color="text.secondary" title="Odpowiedz na komentarz" sx={{
                  display: 'flex', alignItems: 'center'
                }}>
                  <Typography variant="caption">Odpowiedz</Typography>
                </StyledLink>
              </Stack>
            </Stack>
          </Stack>
        </StyledCard>
        {renderVoteBox()}
      </Stack>
      {renderReplyBox()}
    </Stack>
  );
}

CommentItem.propTypes = {
  recipeData: PropTypes.object,
  comment: PropTypes.object
};
